import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle, ArrowDown, ArrowLeft, ArrowUp, Check, Download, ExternalLink, ImagePlus, Loader2, Monitor, Plus, RotateCcw, Send, Trash2, Wand2, X } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { Textarea } from '@orisini/ui/components/textarea'
import { useConfirm } from '@orisini/ui/components/confirm'
import { ApiError } from '@orisini/ui/lib/api'
import { fontFaceCss, type FontData } from '@orisini/studio-kit/fonts'
import { RATIO_SIZE, type Ratio } from '@orisini/studio-kit/ratios'
import { assetIds, buildDocument, resolveAssets, validateValues } from '@orisini/studio-kit/templates/document'
import { toTemplateDef, type DataField, type DataTemplate } from '@orisini/studio-kit/templates/data-template'
import type { Values } from '@orisini/studio-kit/templates/types'
import PosterPreview from '../components/PosterPreview'
import { loadFonts } from '../lib/fonts'
import { photoDataUrl, shrinkPhoto, uploadPhoto } from '../lib/photos'
import { StatusBadges } from '../components/ContentList'
import { contentApi, describeEvent, formatDuration, when, type ContentAction, type ContentDetail, type Copy, type Kind, type VideoDetail } from '../lib/content'

const SIBUK = ['drafting', 'approved', 'producing']
const POLL_MS = 4000

/** Dokumen preview dari isi yang sedang diedit — sama persis dengan render server. */
function useContentDocs(def: DataTemplate | null, kind: Kind, copy: Copy | null, ratio: Ratio) {
  const [fonts, setFonts] = useState<FontData | null>(null)
  const [docs, setDocs] = useState<string[]>([])
  useEffect(() => {
    loadFonts().then(setFonts).catch(() => undefined)
  }, [])
  const kunci = JSON.stringify([def, copy, ratio])
  useEffect(() => {
    if (!def || !copy || !fonts) return
    let batal = false
    const t = setTimeout(async () => {
      try {
        const css = fontFaceCss(def.fonts, fonts)
        const ref = { code: 'edit', version: 0 }
        const sets = kind === 'poster' ? [copy.values] : (copy.slides ?? []).map((s) => ({ ...copy.values, ...s }))
        const out: string[] = []
        for (const [i, values] of sets.entries()) {
          const tpl = kind === 'poster' ? toTemplateDef(def, ref) : toTemplateDef(def, ref, { index: i + 1, total: sets.length, narration: kind === 'video' ? (values.narration ?? '') : undefined })
          const v = validateValues(tpl.fields, values, { draft: true })
          const urls: Record<string, string> = {}
          for (const id of assetIds(tpl.fields, v)) urls[id] = await photoDataUrl(id).catch(() => '')
          out.push(buildDocument(tpl, resolveAssets(tpl.fields, v, urls), RATIO_SIZE[ratio], css))
        }
        if (!batal) setDocs(out)
      } catch {
        // Isi sementara tidak sah (mis. warna setengah diketik): biarkan preview lama.
      }
    }, 200)
    return () => {
      batal = true
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kunci, fonts])
  return docs
}

/** /content/:id — satu konten: isi, preview, hasil render, aksi, dan riwayatnya. */
export default function ContentPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [data, setData] = useState<ContentDetail | null>(null)
  const [copy, setCopy] = useState<Copy | null>(null)
  const [saved, setSaved] = useState('')
  const [missing, setMissing] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const muat = useCallback(async () => {
    try {
      const d = await contentApi.get(id)
      setData(d)
      // Isi editor hanya ditimpa kalau user belum mengubah apa pun.
      setCopy((cur) => (cur && JSON.stringify(cur) !== saved ? cur : d.copy))
      setSaved(JSON.stringify(d.copy))
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setMissing(true)
      else toast.error(err instanceof Error ? err.message : "Couldn't load this content")
    }
  }, [id, saved])

  useEffect(() => {
    void muat()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const sibuk = data ? SIBUK.includes(data.content.productionStatus) : false
  useEffect(() => {
    if (!sibuk) return
    const t = setInterval(() => void muat(), POLL_MS)
    return () => clearInterval(t)
  }, [sibuk, muat])

  const ratio = (data?.content.kind === 'video' ? '9:16' : (data?.files[0]?.ratio ?? (data?.content.productCode === 'pinboss' ? '2:3' : '4:5'))) as Ratio
  const docs = useContentDocs(data?.template.def ?? null, data?.content.kind ?? 'poster', copy, ratio)
  const dirty = copy !== null && JSON.stringify(copy) !== saved

  if (missing) {
    return (
      <div className="space-y-2">
        <h1 className="text-xl font-semibold">Content not found</h1>
        <Link to="/bank" className="text-sm text-muted-foreground hover:text-foreground">Back to the Content Bank</Link>
      </div>
    )
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    )
  }

  const c = data.content
  const bisaEdit = data.workflow.allowEdit && ['draft', 'ready', 'failed'].includes(c.productionStatus) && !['queued', 'scheduled'].includes(c.publishStatus)

  const simpan = async () => {
    if (!copy) return false
    setBusy('save')
    try {
      await contentApi.save(id, copy)
      setSaved(JSON.stringify(copy))
      return true
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save")
      return false
    } finally {
      setBusy(null)
    }
  }

  const jalankan = async (action: ContentAction, pesan: string) => {
    if (action === 'publish') {
      const ok = await confirm({ title: 'Post this now?', description: `It goes to ${c.workspaceName} on ${c.platform} as soon as Orisini Agent picks it up.`, confirmLabel: 'Post now' })
      if (!ok) return
    }
    if (action === 'regenerate' && dirty) {
      const ok = await confirm({ title: 'Rewrite the draft?', description: 'Your unsaved changes are lost and Orisini writes it again from the source.', confirmLabel: 'Rewrite', destructive: true })
      if (!ok) return
    }
    if (dirty && (action === 'approve' || action === 'render') && !(await simpan())) return
    setBusy(action)
    try {
      await contentApi.act(id, action)
      toast.success(pesan)
      await muat()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
      // Kegagalan publikasi tercatat di konten; muat ulang supaya pesannya tetap terlihat.
      await muat()
    } finally {
      setBusy(null)
    }
  }

  const hapus = async () => {
    const ok = await confirm({ title: 'Delete this content?', description: 'Its draft and images are removed. Posts already published stay on the platform.', confirmLabel: 'Delete', destructive: true })
    if (!ok) return
    try {
      await contentApi.remove(id)
      toast.success('Deleted')
      navigate('/bank', { replace: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't delete")
    }
  }

  const def = data.template.def
  const pinterestCarousel = c.productCode === 'pinboss' && c.kind === 'carousel'

  return (
    <div className="space-y-6">
      <Link to={c.productionStatus === 'draft' ? '/review' : '/bank'} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> {c.productionStatus === 'draft' ? 'Review' : 'Content Bank'}
      </Link>

      <header className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">{c.title}</h1>
        <p className="text-sm text-muted-foreground">
          {[
            c.workspaceName,
            c.platform,
            c.kind === 'poster' ? 'Poster' : `${c.kind === 'video' ? 'Video' : 'Carousel'}${c.style ? ` · ${c.style}` : ''}`,
            c.kind === 'video' && c.durationSec ? formatDuration(c.durationSec) : null,
            data.template.ref === 'default' ? 'default design' : `design v${data.template.version}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <StatusBadges item={c} />
        {c.error && (
          <p className="flex items-start gap-1.5 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" /> {c.error}
          </p>
        )}
      </header>

      <div className="flex flex-wrap gap-2">
        {c.productionStatus === 'draft' && (
          <>
            <Button disabled={!!busy} onClick={() => jalankan('approve', 'Approved — producing now')}>
              {busy === 'approve' ? <Loader2 className="animate-spin" /> : <Check />} Approve
            </Button>
            <Button variant="outline" disabled={!!busy} onClick={() => jalankan('reject', 'Rejected')}>
              <X /> Reject
            </Button>
          </>
        )}
        {c.productionStatus === 'ready' && dirty && (
          <Button disabled={!!busy} onClick={() => jalankan('render', 'Saved — rendering again')}>
            {busy === 'render' ? <Loader2 className="animate-spin" /> : <RotateCcw />} Save &amp; render again
          </Button>
        )}
        {c.productionStatus === 'ready' && !dirty && c.productCode && !pinterestCarousel && ['none', 'awaiting_approval', 'failed'].includes(c.publishStatus) && (
          <>
            <Button disabled={!!busy} onClick={() => jalankan('queue', 'Added to the queue')}>
              {busy === 'queue' ? <Loader2 className="animate-spin" /> : <Send />} Add to queue
            </Button>
            <Button variant="outline" disabled={!!busy} onClick={() => jalankan('publish', 'Sent to post now')}>
              Post now
            </Button>
          </>
        )}
        {c.productionStatus === 'failed' && (
          <Button disabled={!!busy} onClick={() => jalankan(data.copy ? 'render' : 'regenerate', 'Trying again')}>
            <RotateCcw /> Try again
          </Button>
        )}
        {data.workflow.allowRegenerate && ['draft', 'ready', 'failed'].includes(c.productionStatus) && !['queued', 'scheduled'].includes(c.publishStatus) && (
          <Button variant="ghost" disabled={!!busy} onClick={() => jalankan('regenerate', 'Rewriting the draft')}>
            <Wand2 /> Rewrite
          </Button>
        )}
        {c.productionStatus === 'draft' && dirty && (
          <Button variant="ghost" disabled={!!busy} onClick={() => simpan().then((ok) => ok && toast.success('Saved'))}>
            {busy === 'save' && <Loader2 className="animate-spin" />} Save
          </Button>
        )}
        <Button variant="ghost" disabled={!!busy} onClick={hapus} aria-label="Delete" title="Delete" className="ml-auto">
          <Trash2 />
        </Button>
      </div>

      {sibuk && (c.kind !== 'video' || c.productionStatus !== 'producing') && (
        <Card className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {c.productionStatus === 'drafting' ? (c.kind === 'video' && data.video?.voiceover ? 'Transcribing your voice-over and writing the scenes…' : 'Reading the story and writing the draft…') : 'Designing and rendering…'} This page updates by itself.
        </Card>
      )}
      {c.kind === 'video' && c.productionStatus === 'producing' && data.video && <RenderStatus video={data.video} />}

      {copy && (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_auto]">
          <div className="space-y-5">
            <Fields fields={def.fields} values={copy.values} disabled={!bisaEdit} onChange={(values) => setCopy({ ...copy, values })} />
            {c.kind !== 'poster' && (
              <Slides def={def} kind={c.kind} slides={copy.slides ?? []} disabled={!bisaEdit} voiceover={Boolean(copy.voiceover)} voice={data.video?.voice ?? []} onChange={(slides) => setCopy({ ...copy, slides })} />
            )}
            <Card className="space-y-3 p-4">
              <div className="space-y-1.5">
                <Label htmlFor="caption">Caption</Label>
                <Textarea id="caption" rows={5} maxLength={2000} disabled={!bisaEdit} value={copy.caption} onChange={(e) => setCopy({ ...copy, caption: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tags">Hashtags</Label>
                <Input
                  id="tags"
                  disabled={!bisaEdit}
                  value={copy.hashtags.join(' ')}
                  placeholder="ai tech news"
                  onChange={(e) => setCopy({ ...copy, hashtags: e.target.value.split(/[\s,]+/).map((h) => h.replace(/^#/, '')).filter(Boolean).slice(0, 8) })}
                />
              </div>
            </Card>
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-semibold">{dirty ? 'Preview (unsaved)' : 'Preview'}</h2>
            {c.kind === 'video' && data.files.some((f) => f.role === 'video') && (
              <VideoResult files={data.files} />
            )}
            <div className="flex max-w-[22rem] flex-wrap gap-2 lg:max-w-[22rem]">
              {docs.map((d, i) => (
                <PosterPreview key={i} doc={d} width={RATIO_SIZE[ratio].width} height={RATIO_SIZE[ratio].height} displayWidth={c.kind === 'poster' ? 320 : c.kind === 'video' ? 100 : 150} label={c.kind === 'poster' ? ratio : `${c.kind === 'video' ? 'Scene' : 'Slide'} ${i + 1}`} />
              ))}
            </div>
            {c.kind !== 'video' && data.files.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-sm font-semibold">Rendered</h2>
                <div className="flex flex-wrap gap-2">
                  {data.files.map((f) => (
                    <a key={f.id} href={`${f.url}?download=1`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:border-foreground/40">
                      <Download className="size-3" /> {c.kind === 'carousel' ? `Slide ${f.position + 1}` : 'PNG'} · {f.width}×{f.height}
                    </a>
                  ))}
                </div>
                {!c.productCode && (
                  <p className="text-xs text-muted-foreground">
                    This project has no account, so download the files and post them yourself — or{' '}
                    <Link to={`/projects/${c.workspaceId}/connection`} className="text-foreground underline-offset-4 hover:underline">connect one</Link>.
                  </p>
                )}
                {pinterestCarousel && <p className="text-xs text-muted-foreground">Pinterest takes single images, so download this carousel and post it elsewhere.</p>}
              </div>
            )}
          </div>
        </div>
      )}

      <Card className="space-y-2 p-4 text-sm">
        <h2 className="font-semibold">Source</h2>
        {data.source.url ? (
          <a href={data.source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:underline">
            {data.source.title} <ExternalLink className="size-3.5" />
          </a>
        ) : (
          <p>{data.source.title}</p>
        )}
        {data.source.partial && <p className="text-xs text-amber-400">Only part of the article could be read (paywall or a site that blocks readers). Check the facts before approving.</p>}
        {data.source.notes && <p className="text-xs text-muted-foreground">Your notes: {data.source.notes}</p>}
        {data.source.text && (
          <details>
            <summary className="cursor-pointer text-xs text-muted-foreground">Text Orisini read ({data.source.text.length.toLocaleString('en-GB')} characters)</summary>
            <p className="mt-2 whitespace-pre-line text-xs text-muted-foreground">{data.source.text.slice(0, 4000)}</p>
          </details>
        )}
      </Card>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">History</h2>
        <ol className="space-y-1.5 border-l border-border pl-4">
          {data.history.map((e, i) => (
            <li key={i} className="text-sm">
              <span className="text-muted-foreground">{when(e.at)}</span> · {describeEvent(e)}
            </li>
          ))}
        </ol>
      </section>
    </div>
  )
}

function Fields({ fields, values, disabled, onChange }: { fields: readonly DataField[]; values: Values; disabled: boolean; onChange: (v: Values) => void }) {
  if (!fields.length) return null
  const set = (k: string, v: string | undefined) => {
    const next = { ...values }
    if (v === undefined || v === '') delete next[k]
    else next[k] = v
    onChange(next)
  }
  return (
    <Card className="space-y-3 p-4">
      {fields.map((f) => (
        <Field key={f.key} field={f} value={values[f.key]} disabled={disabled} onChange={(v) => set(f.key, v)} />
      ))}
    </Card>
  )
}

function Field({ field: f, value, disabled, onChange }: { field: DataField; value: string | undefined; disabled: boolean; onChange: (v: string | undefined) => void }) {
  const [uploading, setUploading] = useState(false)
  const id = useMemo(() => `f-${f.key}-${Math.random().toString(36).slice(2, 7)}`, [f.key])
  const label = (
    <Label htmlFor={id} className="flex justify-between">
      <span>
        {f.label}
        {!f.required && <span className="text-muted-foreground"> (optional)</span>}
      </span>
      {f.maxLength && f.type !== 'image' && f.type !== 'color' && (
        <span className="text-xs text-muted-foreground tabular-nums">
          {(value ?? '').length}/{f.maxLength}
        </span>
      )}
    </Label>
  )
  if (f.type === 'image') {
    const pilih = async (file: File | undefined) => {
      if (!file) return
      setUploading(true)
      try {
        const r = await uploadPhoto(await shrinkPhoto(file))
        onChange(r.ref)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Couldn't upload the photo")
      } finally {
        setUploading(false)
      }
    }
    return (
      <div className="space-y-1.5">
        {label}
        <div className="flex items-center gap-2">
          <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm ${disabled ? 'pointer-events-none opacity-50' : 'hover:border-foreground/40'}`}>
            {uploading ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
            {value ? 'Replace photo' : 'Add photo'}
            <input id={id} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" disabled={disabled} onChange={(e) => void pilih(e.target.files?.[0])} />
          </label>
          {value && !disabled && (
            <Button size="sm" variant="ghost" onClick={() => onChange(undefined)}>
              Remove
            </Button>
          )}
        </div>
      </div>
    )
  }
  return (
    <div className="space-y-1.5">
      {label}
      {f.type === 'color' ? (
        <input id={id} type="color" disabled={disabled} value={value || f.default || '#000000'} onChange={(e) => onChange(e.target.value)} className="h-9 w-16 rounded border border-border bg-transparent" />
      ) : f.type === 'text' ? (
        <Input id={id} disabled={disabled} value={value ?? ''} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <Textarea id={id} disabled={disabled} rows={f.type === 'list' ? 4 : 3} value={value ?? ''} maxLength={f.maxLength} onChange={(e) => onChange(e.target.value)} placeholder={f.type === 'list' ? 'One item per line' : undefined} />
      )}
    </div>
  )
}

/** Status render video: Container (berkredit) atau menunggu Orisini Agent. */
function RenderStatus({ video }: { video: VideoDetail }) {
  const job = video.job
  const agent = (job?.location ?? video.location) === 'agent'
  const pct = job?.progress != null ? Math.round(job.progress * 100) : null
  return (
    <Card className="space-y-2 p-4 text-sm">
      <p className="flex items-center gap-2 text-muted-foreground">
        {agent ? <Monitor className="size-4" /> : <Loader2 className="size-4 animate-spin" />}
        {!job || job.status === 'queued'
          ? agent
            ? 'Waiting for Orisini Agent on your computer to pick this up. Videos over 10 minutes are always rendered there.'
            : 'Recording the voice and preparing the scenes…'
          : agent
            ? `Rendering on your computer${pct !== null ? ` — ${pct}%` : ''}…`
            : `Rendering the video${pct !== null ? ` — ${pct}%` : ''}…`}
      </p>
      {video.totalSec ? (
        <p className="text-xs text-muted-foreground">
          {formatDuration(video.totalSec)} long{job && job.credits > 0 ? ` · uses ${job.credits} video ${job.credits === 1 ? 'minute' : 'minutes'}` : agent ? ' · no video minutes used' : ''}. This page updates by itself.
        </p>
      ) : null}
      {pct !== null && (
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      )}
    </Card>
  )
}

function VideoResult({ files }: { files: ContentDetail['files'] }) {
  const v = files.find((f) => f.role === 'video')!
  const thumb = files.find((f) => f.role === 'thumb')
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold">Video</h2>
      <video controls preload="metadata" poster={thumb?.url} src={v.url} className="w-[16rem] rounded-md border border-border bg-black" style={{ aspectRatio: `${v.width} / ${v.height}` }} />
      <a href={`${v.url}?download=1`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:border-foreground/40">
        <Download className="size-3" /> MP4 · {v.width}×{v.height}
        {v.durationMs ? ` · ${formatDuration(v.durationMs / 1000)}` : ''}
      </a>
    </div>
  )
}

function Slides({
  def,
  kind,
  slides,
  disabled,
  voiceover,
  voice,
  onChange,
}: {
  def: DataTemplate
  kind: Kind
  slides: Values[]
  disabled: boolean
  voiceover: boolean
  voice: VideoDetail['voice']
  onChange: (s: Values[]) => void
}) {
  const fields = def.slideFields ?? []
  const video = kind === 'video'
  const nama = video ? 'Scene' : 'Slide'
  // Voice-over unggahan: adegan mengikuti rekaman, jadi tidak bisa ditambah/dihapus/dipindah.
  const tetap = disabled || voiceover
  const pindah = (i: number, d: -1 | 1) => {
    const next = [...slides]
    const [x] = next.splice(i, 1)
    next.splice(i + d, 0, x!)
    onChange(next)
  }
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold">{nama}s</h2>
      {slides.map((s, i) => (
        <Card key={i} className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {nama} {i + 1}
            </span>
            {!tetap && (
              <span className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => pindah(i, -1)} aria-label="Move up">
                  <ArrowUp />
                </Button>
                <Button size="sm" variant="ghost" disabled={i === slides.length - 1} onClick={() => pindah(i, 1)} aria-label="Move down">
                  <ArrowDown />
                </Button>
                <Button size="sm" variant="ghost" disabled={slides.length <= 2} onClick={() => onChange(slides.filter((_, k) => k !== i))} aria-label="Remove slide">
                  <Trash2 />
                </Button>
              </span>
            )}
          </div>
          {video && (
            <div className="space-y-1.5">
              <Label className="flex justify-between">
                <span>Narration</span>
                <span className="text-xs text-muted-foreground">{voiceover ? 'from your voice-over' : `${(s.narration ?? '').length}/1200`}</span>
              </Label>
              <Textarea
                rows={2}
                maxLength={1200}
                disabled={disabled || voiceover}
                value={s.narration ?? ''}
                placeholder="What the voice says in this scene"
                onChange={(e) => {
                  const next = [...slides]
                  next[i] = { ...s, narration: e.target.value }
                  onChange(next)
                }}
              />
              {voice[i] && !voiceover && <audio controls preload="none" src={voice[i]!.url} className="h-8 w-full" />}
            </div>
          )}
          {fields.map((f) => (
            <Field
              key={f.key}
              field={f}
              value={s[f.key]}
              disabled={disabled}
              onChange={(v) => {
                const next = [...slides]
                const sl = { ...s }
                if (v === undefined || v === '') delete sl[f.key]
                else sl[f.key] = v
                next[i] = sl
                onChange(next)
              }}
            />
          ))}
        </Card>
      ))}
      {!tetap && slides.length < (video ? 150 : 10) && (
        <Button variant="outline" size="sm" onClick={() => onChange([...slides, {}])}>
          <Plus /> Add {nama.toLowerCase()}
        </Button>
      )}
    </section>
  )
}
